/**
 * BasicsSection.js — Section 1 of the Mechanic Composer.
 *
 * Captures meta fields: name, description, containerContext, containerType,
 * domain, cardType, and level. Conditional fields shown based on context.
 *
 * Part of the Homebrew Workshop (Issue #82, Phase E: #180)
 */

import React from 'react';
import {
  VALID_CONTAINER_TYPES,
  VALID_DOMAINS,
  VALID_CARD_TYPES,
} from '../../../../engine/schema';

const inputClass =
  'w-full bg-gray-800 border border-gray-600 rounded-md px-3 py-2 text-white focus:border-red-500 focus:ring-1 focus:ring-red-500 outline-none transition-colors';

const labelClass = 'block text-sm font-medium text-gray-300 mb-1';

const errorClass = 'text-red-400 text-xs mt-1';

export default function BasicsSection({ config, dispatch, errors }) {
  const meta = config?.meta || {};
  const isDomainCard = meta.containerContext === 'domain-card';
  const fieldErrors = errors || [];

  const handleMetaChange = (field, value) => {
    dispatch({ type: 'SET_META_FIELD', field, value });
  };

  return (
    <div className="space-y-4">
      {/* Name */}
      <div>
        <label className={labelClass}>
          Name <span className="text-red-400">*</span>
        </label>
        <input
          type="text"
          value={meta.name || ''}
          onChange={(e) => handleMetaChange('name', e.target.value)}
          placeholder="e.g., Crystal Resonance"
          className={inputClass}
          maxLength={80}
        />
        {fieldErrors.some(e => e.toLowerCase().includes('name')) && (
          <p className={errorClass}>Name is required</p>
        )}
      </div>

      {/* Description */}
      <div>
        <label className={labelClass}>Description</label>
        <textarea
          value={meta.description || ''}
          onChange={(e) => handleMetaChange('description', e.target.value)}
          placeholder="Describe what this mechanic does..."
          className={`${inputClass} resize-y min-h-[80px]`}
          rows={3}
        />
      </div>

      {/* Container Context */}
      <div>
        <label className={labelClass}>Type</label>
        <p className="text-xs text-gray-500 mb-2">Abilities are class/ancestry features. Domain Cards are drawn from your domain deck.</p>
        <div className="flex gap-3">
          <button
            type="button"
            onClick={() => handleMetaChange('containerContext', 'ability')}
            className={`flex-1 px-4 py-2 rounded-md border text-sm font-medium transition-colors ${
              meta.containerContext === 'ability'
                ? 'bg-red-900/40 border-red-600 text-red-300'
                : 'bg-gray-800 border-gray-600 text-gray-400 hover:border-gray-500'
            }`}
          >
            Ability
          </button>
          <button
            type="button"
            onClick={() => handleMetaChange('containerContext', 'domain-card')}
            className={`flex-1 px-4 py-2 rounded-md border text-sm font-medium transition-colors ${
              meta.containerContext === 'domain-card'
                ? 'bg-red-900/40 border-red-600 text-red-300'
                : 'bg-gray-800 border-gray-600 text-gray-400 hover:border-gray-500'
            }`}
          >
            Domain Card
          </button>
        </div>
      </div>

      {/* Container Type */}
      <div>
        <label className={labelClass}>Source Type</label>
        <p className="text-xs text-gray-500 mb-1">Where this mechanic comes from (class, ancestry, community, etc.)</p>
        <select
          value={meta.containerType || 'class'}
          onChange={(e) => handleMetaChange('containerType', e.target.value)}
          className={inputClass}
        >
          {VALID_CONTAINER_TYPES.map(t => (
            <option key={t} value={t}>
              {t.charAt(0).toUpperCase() + t.slice(1)}
            </option>
          ))}
        </select>
      </div>

      {/* Domain (domain-card only) */}
      {isDomainCard && (
        <div>
          <label className={labelClass}>Domain</label>
          <select
            value={meta.domain || ''}
            onChange={(e) => handleMetaChange('domain', e.target.value)}
            className={inputClass}
          >
            <option value="">Select domain...</option>
            {VALID_DOMAINS.map(d => (
              <option key={d} value={d}>
                {d.charAt(0).toUpperCase() + d.slice(1)}
              </option>
            ))}
          </select>
        </div>
      )}

      {/* Card Type (domain-card only) */}
      {isDomainCard && (
        <div>
          <label className={labelClass}>Card Type</label>
          <select
            value={meta.cardType || ''}
            onChange={(e) => handleMetaChange('cardType', e.target.value)}
            className={inputClass}
          >
            <option value="">Select card type...</option>
            {VALID_CARD_TYPES.map(ct => (
              <option key={ct} value={ct}>{ct}</option>
            ))}
          </select>
        </div>
      )}

      {/* Level */}
      <div>
        <label className={labelClass}>Level (optional)</label>
        <p className="text-xs text-gray-500 mb-1">The character level when this becomes available</p>
        <input
          type="number"
          min={1}
          max={10}
          value={meta.level || ''}
          onChange={(e) => {
            const val = e.target.value ? parseInt(e.target.value, 10) : undefined;
            handleMetaChange('level', val);
          }}
          placeholder="1-10"
          className={`${inputClass} w-24`}
        />
      </div>
    </div>
  );
}
